import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Target } from 'chery-contracts';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { orpc } from '../../apis/orpc-query';
import { useAuth } from '../../auth/AuthContext';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Notice, type NoticeTone } from '../../components/ui/Notice';
import { Spinner } from '../../components/ui/Spinner';
import { Table, TableCell, TableHead, TableHeaderCell, TableRow } from '../../components/ui/Table';
import { cn } from '../../lib/cn';
import { PILLAR_CLASSES } from '../../lib/pillars';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { canManageTargets, directionText, matchesTargetSearch, periodText, targetValueText } from './target-display';
import { TargetSheet, type TargetSheetMode } from './TargetSheet';

interface PageNotice {
  tone: NoticeTone;
  text: string;
}

type SheetState = { mode: 'create' } | { mode: Exclude<TargetSheetMode, 'create'>; target: Target } | null;

/**
 * Targets setup (plan §7): every target with a KPI the caller may see — value and direction, period, linked KPIs
 * by pillar, status. ADMIN and PM create and edit in the `TargetSheet`; everybody else gets the same list read-only.
 */
export const TargetsPage = () => {
  const { t } = useI18nTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canManage = canManageTargets(user?.role);

  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState<PageNotice | null>(null);
  const [sheet, setSheet] = useState<SheetState>(null);

  const targetsQuery = useQuery(orpc.targets.list.queryOptions({ input: {} }));
  const kpisQuery = useQuery(orpc.kpis.list.queryOptions({ input: { includeInactive: true } }));
  const weeksQuery = useQuery(orpc.weeks.list.queryOptions({ input: {} }));

  const targets = useMemo(
    () => (targetsQuery.data?.items ?? []).filter((target) => matchesTargetSearch(target, search)),
    [targetsQuery.data, search],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('pages.setupTargets')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{canManage ? t('targets.subtitle') : t('targets.readOnly')}</p>
        </div>
        {canManage ? (
          <Button
            data-new-target
            onClick={() => setSheet({ mode: 'create' })}
          >
            <Plus className="size-4" />
            {t('targets.new')}
          </Button>
        ) : null}
      </div>

      {notice ? (
        <Notice
          tone={notice.tone}
          onDismiss={() => setNotice(null)}
        >
          {notice.text}
        </Notice>
      ) : null}

      <Input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('targets.search')}
        className="max-w-xs"
      />

      <Table>
        <TableHead>
          <tr>
            <TableHeaderCell>{t('targets.columns.name')}</TableHeaderCell>
            <TableHeaderCell className="text-end">{t('targets.columns.value')}</TableHeaderCell>
            <TableHeaderCell>{t('targets.columns.period')}</TableHeaderCell>
            <TableHeaderCell>{t('targets.columns.kpis')}</TableHeaderCell>
            <TableHeaderCell>{t('targets.columns.status')}</TableHeaderCell>
          </tr>
        </TableHead>
        <tbody>
          {targetsQuery.isLoading ? (
            <tr>
              <td
                colSpan={5}
                className="px-4 py-12 text-center text-muted-foreground"
              >
                <span className="inline-flex items-center gap-2">
                  <Spinner />
                  {t('app.loading')}
                </span>
              </td>
            </tr>
          ) : targets.length === 0 ? (
            <tr>
              <td
                colSpan={5}
                className="px-4 py-12 text-center text-muted-foreground"
              >
                {targetsQuery.isError ? t('errors.loadFailed') : t('targets.empty')}
              </td>
            </tr>
          ) : (
            targets.map((target) => (
              <TableRow
                key={target.id}
                data-target-name={target.name}
                onClick={() => setSheet({ mode: canManage ? 'edit' : 'view', target })}
                className={cn('cursor-pointer transition-colors hover:bg-muted/60', target.status === 'INACTIVE' && 'text-muted-foreground')}
              >
                <TableCell className="font-medium">{target.name}</TableCell>
                <TableCell className="whitespace-nowrap text-end font-mono text-xs">
                  <span className="text-muted-foreground">{directionText(target.direction)}</span> {targetValueText(target)}
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs">{periodText(target)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {target.kpis.map((kpi) => (
                      <Badge
                        key={kpi.id}
                        className={cn('border', PILLAR_CLASSES[kpi.pillar].border, PILLAR_CLASSES[kpi.pillar].text)}
                      >
                        {kpi.name}
                      </Badge>
                    ))}
                    {target.hiddenKpiCount > 0 ? <Badge tone="outline">{t('targets.hiddenKpis', { count: target.hiddenKpiCount })}</Badge> : null}
                  </div>
                </TableCell>
                <TableCell>
                  {target.status === 'INACTIVE' ? (
                    <Badge tone="outline">{t('enums.status.INACTIVE')}</Badge>
                  ) : (
                    <span className="text-xs">{t('enums.status.ACTIVE')}</span>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </tbody>
      </Table>

      <TargetSheet
        isOpen={sheet !== null}
        mode={sheet?.mode ?? 'create'}
        target={sheet && sheet.mode !== 'create' ? sheet.target : undefined}
        kpis={kpisQuery.data?.items ?? []}
        weeks={weeksQuery.data?.items ?? []}
        onClose={() => setSheet(null)}
        onSaved={(name, created) => {
          void queryClient.invalidateQueries({ queryKey: orpc.targets.key() });
          void queryClient.invalidateQueries({ queryKey: orpc.tracker.key() });
          setSheet(null);
          setNotice({ tone: 'success', text: created ? t('targets.created', { name }) : t('targets.saved', { name }) });
        }}
      />
    </div>
  );
};
